import { useCallback, useEffect, useMemo, useState } from 'react';

import { RadioOptionsWithConfirm, IQuestion } from 'shared-components';
import { AccommodationTypeStatusEnum } from 'types';

interface IProps {
  setComplete: (status: boolean) => void;
  showWarning: boolean;
  setShowWarning: (status: boolean) => void;
  additionalAnswers: {
    setStatus: (status: AccommodationTypeStatusEnum) => void;
  };
  accommodationTypeStatus: AccommodationTypeStatusEnum;
  isArranged: boolean;
  universityAccommodationEnabled: boolean;
}
export const AccommodationTypeArranged = (props: IProps) => {
  const {
    setComplete,
    showWarning,
    setShowWarning,
    additionalAnswers: { setStatus },
    accommodationTypeStatus,
    isArranged,
    universityAccommodationEnabled
  } = props;

  const [selection, setSelection] = useState(accommodationTypeStatus || AccommodationTypeStatusEnum.NONE);

  useEffect(() => {
    const selectableStatuses = Object.values(AccommodationTypeStatusEnum)
      .filter(s => s !== AccommodationTypeStatusEnum.NONE)
      .filter(s =>
        isArranged ? s !== AccommodationTypeStatusEnum.I_DONT_KNOW : s !== AccommodationTypeStatusEnum.OTHER
      );

    const hasWarning =
      !universityAccommodationEnabled && selection === AccommodationTypeStatusEnum.UNIVERSITY_ACCOMMODATION;

    if (hasWarning) {
      setShowWarning(true);
    } else if (showWarning) {
      setShowWarning(false);
    }

    if (!hasWarning && selectableStatuses.includes(selection as AccommodationTypeStatusEnum)) {
      setStatus(selection as AccommodationTypeStatusEnum);

      setComplete(true);
    } else {
      setComplete(false);
    }
  }, [selection, setStatus, setComplete, isArranged, universityAccommodationEnabled, setShowWarning, showWarning]);

  const questions = useMemo(
    () => [
      {
        char: '1',
        label: 'University Accommodation',
        selected: selection === AccommodationTypeStatusEnum.UNIVERSITY_ACCOMMODATION,
        status: AccommodationTypeStatusEnum.UNIVERSITY_ACCOMMODATION
      },
      {
        char: '2',
        label: `Private Student Accommodation`,
        selected: selection === AccommodationTypeStatusEnum.PRIVATE_STUDENT_ACCOMMODATION,
        status: AccommodationTypeStatusEnum.PRIVATE_STUDENT_ACCOMMODATION
      },
      {
        char: '3',
        label: 'Private Rental',
        selected: selection === AccommodationTypeStatusEnum.PRIVATE_RENTAL,
        status: AccommodationTypeStatusEnum.PRIVATE_RENTAL
      },
      {
        char: '4',
        label: 'Staying with Family',
        selected: selection === AccommodationTypeStatusEnum.STAYING_WITH_FAMILY,
        status: AccommodationTypeStatusEnum.STAYING_WITH_FAMILY
      },
      {
        char: '5',
        label: 'Homestay',
        selected: selection === AccommodationTypeStatusEnum.HOMESTAY,
        status: AccommodationTypeStatusEnum.HOMESTAY
      },
      isArranged
        ? {
            char: '6',
            label: 'Other',
            selected: selection === AccommodationTypeStatusEnum.OTHER,
            status: AccommodationTypeStatusEnum.OTHER
          }
        : {
            char: '6',
            label: "I don't know yet",
            selected: selection === AccommodationTypeStatusEnum.I_DONT_KNOW,
            status: AccommodationTypeStatusEnum.I_DONT_KNOW
          }
    ],
    [isArranged, selection]
  );

  const getAccommodationStatusFromLabel = useCallback(
    (label: string) => {
      return questions.find(question => question.label === label)!.status;
    },
    [questions]
  );

  return (
    <div data-selector="enrolment-section-accommodation-status-response">
      <RadioOptionsWithConfirm
        label={isArranged ? `What type of accommodation have you arranged?` : `What option are you considering?`}
        questions={questions}
        onClick={(question: IQuestion) => {
          setSelection(getAccommodationStatusFromLabel(question.label));
        }}
      />
    </div>
  );
};
